import React, { useState, useEffect, useRef, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { toSlug, CATEGORY_STYLE, DEFAULT_STYLE } from '../api/services.api';
import { useCategories, useServices, toArray } from '../hooks/useCatalog';
import MakeYourOwnPackageModal from '../components/MakeYourOwnPackageModal';
import { MENS_PACKAGE } from '../data/customFlows';
import Header from '../../../shared/components/Header';
import Footer from '../../../shared/components/Footer';

/* ─── Keyframes ─── */
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ─── Global scroll reset ─── */
const GlobalScrollFix = createGlobalStyle`
  html, body {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    height: auto !important;
  }
`;

/* ─── Horizontal scroll containers ─── */
const Scroll = styled.div`
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

/* Subcategory grid (3 cols on desktop) */
const CcGrid = styled(Scroll)`
  @media (min-width: 768px) {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 20px !important;
    overflow: visible !important;
  }
`;

/* Inline service grid (4 cols on desktop) */
const SvcGrid = styled(Scroll)`
  @media (min-width: 768px) {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr) !important;
    gap: 16px !important;
    overflow: visible !important;
  }
`;

const CcCell = styled.div`
  @media (min-width: 768px) {
    flex: unset !important;
    min-width: unset !important;
  }
`;

const SvcCell = styled.div`
  @media (min-width: 768px) {
    flex: unset !important;
    min-width: unset !important;
  }
`;

/* Icon tile */
const Tile = styled.div`
  cursor: pointer;
  flex-shrink: 0;
`;

const TileRing = styled.div`
  border-radius: 50%;
  overflow: hidden;
  transition: border-color 0.18s, box-shadow 0.18s;
`;

const TileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1);

  ${Tile}:hover & {
    transform: scale(1.08);
  }
`;

/* Subcategory card */
const CcCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease, border-color 0.18s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 36px rgba(0,0,0,0.11) !important;
  }
`;

const CcCardPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s cubic-bezier(0.16,1,0.3,1);

  ${CcCard}:hover & {
    transform: scale(1.05);
  }
`;

const ExploreBtn = styled.button`
  border: none;
  cursor: pointer;
  transition: background 0.18s, opacity 0.15s, transform 0.12s;

  &:hover {
    opacity: 0.88;
    transform: scale(1.02);
  }
  &::before, &::after { display: none !important; }
`;


const Skel = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 8px;
`;

/* Inline service card */
const SvcCardBox = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s cubic-bezier(0.16,1,0.3,1), box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important;
  }
`;

const SvcCardPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.32s cubic-bezier(0.16,1,0.3,1);

  ${SvcCardBox}:hover & {
    transform: scale(1.06);
  }
`;

const CcMobileHint = styled.p`
  @media (min-width: 768px) {
    display: none !important;
  }
`;

const SvcMobileHint = styled.p`
  @media (min-width: 768px) {
    display: none !important;
  }
`;

const Page = styled.div`
  background: #f3f4f6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
`;

const HeaderBar = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const AccentStrip = styled.div`
  height: 3px;
  background: linear-gradient(90deg, ${p => p.$from}, ${p => p.$to});
`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const PackageCta = styled.button`
  margin-top: 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border: none;
  cursor: pointer;
  border-radius: 12px;
  background: linear-gradient(135deg, #082189 0%, #1a3aaa 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.1px;
  box-shadow: 0 6px 18px rgba(8, 33, 137, 0.28);
  transition: opacity 0.15s, transform 0.12s;

  &:hover { opacity: 0.93; transform: translateY(-1px); }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
`;

const Crumb = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  padding: 0;
`;

const CrumbSep = styled.span`
  color: #d1d5db;
  font-size: 13px;
`;

const CrumbCurrent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 14px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
`;

const PageH1 = styled.h1`
  margin: 0 0 4px;
  font-size: clamp(22px, 4vw, 30px);
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.4px;
  line-height: 1.15;
`;

const PageSub = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: stretch;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const StatCell = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  text-align: center;
  border-right: ${p => (p.$last ? 'none' : '1px solid #e5e7eb')};

  @media (max-width: 480px) {
    padding: 10px 6px;
  }
`;

const StatValue = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: ${p => p.$color};
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TileRow = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const TileRowInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 18px 0 20px;
`;

const BrowseLabel = styled.p`
  margin: 0 0 14px;
  padding: 0 20px;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const AllTileRing = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  flex-shrink: 0;
  border: ${p => (p.$active ? `3px solid ${p.$accent}` : '3px solid #e5e7eb')};
  box-shadow: ${p => (p.$active ? `0 0 0 3px ${p.$accent}40` : '0 1px 4px rgba(0,0,0,0.08)')};
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  transition: border-color 0.18s, box-shadow 0.18s;
`;

const AllTileLabel = styled.span`
  font-size: 11.5px;
  font-weight: ${p => (p.$active ? 700 : 500)};
  color: ${p => (p.$active ? p.$accent : '#374151')};
  transition: color 0.18s;
`;

const CardsSection = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 0 32px;
`;

const SectionTitleRow = styled.div`
  padding: 0 20px 18px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const SectionH2 = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 800;
  color: #111827;
`;

const SectionCount = styled.span`
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
`;

const ServicesSection = styled.div`
  background: #ffffff;
  border-top: 3px solid ${p => p.$accent};
  border-bottom: 1px solid #e5e7eb;
  padding: 28px 0 36px;
`;

const SvcSectionHeader = styled.div`
  padding: 0 20px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SvcSectionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${p => `${p.$accent}15`};
  border: ${p => `1.5px solid ${p.$accent}30`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const SvcSectionTitleWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const SvcSectionH2 = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  color: #111827;
`;

const SvcSectionCount = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
`;

const CloseBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  cursor: pointer;
`;

const SvcErrorBox = styled.div`
  padding: 20px;
  text-align: center;
  color: #6b7280;
`;

const SvcEmpty = styled.p`
  text-align: center;
  font-size: 14px;
  color: #9ca3af;
  margin: 20px 0 0;
`;

const TrustSection = styled.div`
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 48px 20px;
`;

const TrustH3 = styled.h3`
  margin: 0 0 6px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.3px;
`;

const TrustSub = styled.p`
  margin: 0 0 28px;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;

const WhyIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const WhyTitle = styled.p`
  margin: 0 0 3px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

const WhyBody = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.55;
`;

const ErrorWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const ErrorIcon = styled.span`
  font-size: 40px;
`;

const ErrorTitle = styled.p`
  font-weight: 800;
  font-size: 22px;
  color: #111827;
  margin: 0;
`;

const ErrorMsg = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const RetryBtn = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
`;

const DEFAULT_IMG = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=500&h=340&fit=crop';
const MYOP_IMG = 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=500&h=340&fit=crop';

/* ─── Icon tile (subcategory pill at top) ─── */
const IconTile = memo(({ sub, active, accent, onClick }) => (
  <Tile
    onClick={onClick}
    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '4px 8px', width: 96 }}
  >
    <TileRing
      style={{
        width: 80, height: 80, flexShrink: 0,
        border: active ? `3px solid ${accent}` : '3px solid #e5e7eb',
        boxShadow: active ? `0 0 0 3px ${accent}40` : '0 1px 4px rgba(0,0,0,0.08)',
      }}
    >
      <TileImg
        src={sub.imageURL || sub.imagePath || DEFAULT_IMG}
        alt={sub.subCategoryName}
        onError={e => { e.target.src = DEFAULT_IMG; }}
      />
    </TileRing>
    <span style={{
      fontSize: 11.5,
      fontWeight: active ? 700 : 500,
      color: active ? accent : '#374151',
      textAlign: 'center',
      lineHeight: 1.35,
      maxWidth: 86,
      transition: 'color 0.18s',
    }}>
      {sub.subCategoryName}
    </span>
  </Tile>
));

/* ─── Subcategory card ─── */
const SubCatCard = memo(({ sub, index, accent, isSelected, onSelect }) => (
  <CcCell
    style={{ flex: '0 0 252px', minWidth: 252, animation: `${slideUp.getName()} 0.32s ${index * 0.05}s both`, cursor: 'pointer' }}
    onClick={() => onSelect(sub)}
  >
    <CcCard
      style={isSelected ? { border: `2px solid ${accent}`, boxShadow: `0 0 0 3px ${accent}20` } : undefined}
    >
      <div style={{ height: 184, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
        <CcCardPhoto
          src={sub.imageURL || sub.imagePath || DEFAULT_IMG}
          alt={sub.subCategoryName}
          onError={e => { e.target.src = DEFAULT_IMG; }}
        />
      </div>
      <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <p style={{ margin: '0 0 auto', fontSize: 15, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
          {sub.subCategoryName}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 14 }}>
          <ExploreBtn
            style={{
              padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: isSelected ? accent : '#111827', color: '#ffffff',
            }}
          >
            {isSelected ? 'Selected ✓' : 'View →'}
          </ExploreBtn>
        </div>
      </div>
    </CcCard>
  </CcCell>
));

/* ─── Skeleton card ─── */
const SkeletonCard = () => (
  <CcCell style={{ flex: '0 0 252px', minWidth: 252 }}>
    <CcCard style={{ pointerEvents: 'none' }}>
      <Skel style={{ height: 184 }} />
      <div style={{ padding: '14px 16px 16px' }}>
        <Skel style={{ height: 16, marginBottom: 8 }} />
        <Skel style={{ height: 12, width: '55%', marginBottom: 20 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Skel style={{ height: 34, width: 100, borderRadius: 8 }} />
        </div>
      </div>
    </CcCard>
  </CcCell>
);

/* ─── Inline service card (shown after subcategory selection) ─── */
const SvcCard = memo(({ svc, index, accent, category, subCategoryID }) => {
  const navigate = useNavigate();
  const firstPkg = svc.packages?.[0];
  const viewDetails = (e) => {
    e?.stopPropagation();
    navigate(`/${category}/${subCategoryID}/${svc.serviceID}${firstPkg ? `?pkg=${firstPkg.packageID}` : ''}`);
  };

  return (
    <SvcCell
      style={{ flex: '0 0 220px', minWidth: 220, animation: `${slideUp.getName()} 0.28s ${index * 0.04}s both` }}
      onClick={viewDetails}
    >
      <SvcCardBox>
        <div style={{ height: 160, overflow: 'hidden', flexShrink: 0 }}>
          <SvcCardPhoto
            src={svc.imageURL || svc.bannerURL || DEFAULT_IMG}
            alt={svc.serviceName}
            onError={e => { e.target.src = DEFAULT_IMG; }}
          />
        </div>
        <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
            {svc.serviceName}
          </p>
          {svc.description ? (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
              {svc.description}
            </p>
          ) : null}
          <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex' }}>
            <ExploreBtn
              onClick={viewDetails}
              style={{
                padding: '6px 14px', borderRadius: 7,
                border: 'none', background: '#082189',
                color: '#ffffff', fontSize: 12, fontWeight: 700,
              }}
            >
              View Details →
            </ExploreBtn>
          </div>
        </div>
      </SvcCardBox>
    </SvcCell>
  );
});

/* ─── Inline service skeleton ─── */
const SvcSkel = () => (
  <SvcCell style={{ flex: '0 0 220px', minWidth: 220 }}>
    <SvcCardBox style={{ pointerEvents: 'none', cursor: 'default' }}>
      <Skel style={{ height: 160 }} />
      <div style={{ padding: '12px 14px 14px' }}>
        <Skel style={{ height: 14, marginBottom: 8 }} />
        <Skel style={{ height: 10, width: '60%', marginBottom: 16 }} />
        <Skel style={{ height: 28, width: 80, borderRadius: 14 }} />
      </div>
    </SvcCardBox>
  </SvcCell>
);

/* ─── Page ─── */
const CategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();

  // Categories (subcategory list for this page)
  const { data: catsData, isLoading: loading, error: catsError } = useCategories();
  const apiCats = toArray(catsData);
  const error = catsError ? catsError.message : null;

  // Single selection state — drives both card filter AND service fetch
  const [selectedSub, setSelectedSub] = useState(null);
  const [pkgOpen, setPkgOpen] = useState(false);

  // "Make your own package" is a Glam & Glow (beauty) flow only.
  // Categories that offer "Make your own package" (beauty + men's care).
  const isBeauty = category === 'glam-glow' || category === 'mens-care';
  const packageData = category === 'mens-care' ? MENS_PACKAGE : undefined;

  // Services for the selected subcategory (React Query, enabled when a sub is picked)
  const {
    data: svcData,
    isFetching: svcLoading,
    error: svcErrObj,
  } = useServices(selectedSub?.subCategoryID);
  const rawSvcList = selectedSub ? toArray(svcData) : [];
  // Bathroom & Balcony: first card becomes "Free Bathroom Cleaning" and a new
  // "Regular Bathroom Cleaning" card is inserted right after it (index 1).
  const isBathroomBalcony =
    !!selectedSub &&
    /bathroom\s*(&|and)?\s*balcony/i.test(selectedSub.subCategoryName ?? selectedSub.name ?? '');
  const svcList =
    isBathroomBalcony && rawSvcList.length > 0
      ? [
          { ...rawSvcList[0], serviceName: 'Deep Bathroom Cleaning' },
          { ...rawSvcList[0], serviceName: 'Regular Bathroom Cleaning' },
          ...rawSvcList.slice(1),
        ]
      : rawSvcList;
  const svcError = selectedSub && svcErrObj ? svcErrObj.message : null;

  const cardsRef = useRef(null);
  const servicesRef = useRef(null);

  // Reset scroll/overflow and selection when navigating to a new category
  useEffect(() => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.height = '';
    document.documentElement.style.height = '';
    window.scrollTo({ top: 0 });
    setSelectedSub(null);
  }, [category]);

  // Scroll to services section once it starts loading
  useEffect(() => {
    if (selectedSub) {
      setTimeout(() => servicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [selectedSub]);

  const catData = apiCats.find(c => toSlug(c.categoryName ?? c.name ?? '') === category);
  const style = CATEGORY_STYLE[category] || DEFAULT_STYLE;
  const accent = style.from;
  const rawSubCategories = catData?.subCategories ?? catData?.subcategories ?? [];
  // Fixed ordering: Full Home Cleaning first, Bathroom & Balcony second,
  // Sofa & Carpet last; everything else keeps its API order in between.
  const subRank = (name = '') => {
    const n = name.toLowerCase();
    if (/full\s*home\s*cleaning/.test(n)) return 0;
    if (/bathroom\s*(&|and)?\s*balcony/.test(n)) return 1;
    if (/sofa\s*(&|and)?\s*carpet/.test(n)) return 3;
    return 2;
  };
  const subCategories = [...rawSubCategories].sort(
    (a, b) => subRank(a.subCategoryName ?? a.name) - subRank(b.subCategoryName ?? b.name),
  );

  const displayed = selectedSub
    ? subCategories.filter(s => s.subCategoryID === selectedSub.subCategoryID)
    : subCategories;

  // Unified select handler — used by both icon tiles and subcategory cards
  const handleSelect = (sub) => {
    if (selectedSub?.subCategoryID === sub.subCategoryID) {
      setSelectedSub(null);
    } else {
      setSelectedSub(sub);
      setTimeout(() => cardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  };

  // ── Error state ──
  if (error) {
    return (
      <>
        <GlobalScrollFix />
        <Header />
        <ErrorWrap>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Failed to load services</ErrorTitle>
          <ErrorMsg>{error}</ErrorMsg>
          <RetryBtn onClick={() => window.location.reload()}>
            Try Again
          </RetryBtn>
        </ErrorWrap>
        <Footer />
      </>
    );
  }

  // ── Not found state ──
  if (!loading && !catData) {
    return (
      <>
        <GlobalScrollFix />
        <Header />
        <ErrorWrap>
          <ErrorIcon>🔍</ErrorIcon>
          <ErrorTitle>Category not found</ErrorTitle>
          <RetryBtn onClick={() => navigate('/')}>
            ← Back to Home
          </RetryBtn>
        </ErrorWrap>
        <Footer />
      </>
    );
  }

  const catName = catData?.categoryName ?? catData?.name ?? category.replace(/-/g, ' ');
  const catSubtitle = catData?.subtitle ?? catData?.description ?? 'Professional services at your doorstep';

  return (
    <>
      <GlobalScrollFix />
      <Header />

      <Page>

        {/* ── Page header ── */}
        <HeaderBar>
          <AccentStrip $from={style.from} $to={style.to} />

          <Wrap style={{ padding: '24px 20px 28px' }}>
            <Breadcrumb>
              <Crumb onClick={() => navigate('/')}>
                ← Home
              </Crumb>
              <CrumbSep>›</CrumbSep>
              <CrumbCurrent>{catName}</CrumbCurrent>
            </Breadcrumb>

            <TitleRow>
              <IconBox>
                {style.icon}
              </IconBox>
              <div>
                <PageH1>
                  {catName}
                </PageH1>
                <PageSub>
                  {catSubtitle}
                </PageSub>
              </div>
            </TitleRow>

            <StatsBar>
              {[
                { value: '★ 4.9', label: 'Rating', valueColor: '#111827' },
                { value: `${subCategories.length > 0 ? subCategories.length : '10'}+`, label: 'Services', valueColor: '#111827' },
                { value: '✓ Guaranteed', label: 'Satisfaction', valueColor: '#15803d' },
              ].map((stat, i, arr) => (
                <StatCell key={i} $last={i === arr.length - 1}>
                  <StatValue $color={stat.valueColor}>{stat.value}</StatValue>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCell>
              ))}
            </StatsBar>
{/* 
            {isBeauty && (
              <PackageCta onClick={() => setPkgOpen(true)}>
                ✨ Make your own package
              </PackageCta>
            )} */}
          </Wrap>
        </HeaderBar>

        {/* ── Icon tile row ── */}
        <TileRow>
          <TileRowInner>
            <BrowseLabel>
              Browse services
            </BrowseLabel>

            <Scroll style={{ gap: 0, padding: '0 12px 4px', alignItems: 'flex-start' }}>
              {/* All tile */}
              <Tile
                onClick={() => setSelectedSub(null)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '4px 8px', width: 96 }}
              >
                <AllTileRing $active={selectedSub === null} $accent={accent}>
                  ✦
                </AllTileRing>
                <AllTileLabel $active={selectedSub === null} $accent={accent}>
                  All
                </AllTileLabel>
              </Tile>

              {/* MYOP — Make your own package (Glam & Glow only), right after All */}
              {isBeauty && (
                <Tile
                  onClick={() => setPkgOpen(true)}
                  title="Make your own package"
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '4px 8px', width: 96 }}
                >
                  <AllTileRing $active={pkgOpen} $accent={accent}>
                    ✨
                  </AllTileRing>
                  <AllTileLabel $active={pkgOpen} $accent={accent}>
                    MYOP
                  </AllTileLabel>
                </Tile>
              )}

              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '4px 8px', width: 96 }}>
                      <Skel style={{ width: 80, height: 80, borderRadius: '50%' }} />
                      <Skel style={{ height: 10, width: 60 }} />
                    </div>
                  ))
                : subCategories.map(s => (
                    <IconTile
                      key={s.subCategoryID}
                      sub={s}
                      active={selectedSub?.subCategoryID === s.subCategoryID}
                      accent={accent}
                      onClick={() => handleSelect(s)}
                    />
                  ))
              }
            </Scroll>
          </TileRowInner>
        </TileRow>

        {/* ── Subcategory cards grid ── */}
        <CardsSection ref={cardsRef}>
          <SectionTitleRow>
            <SectionH2>
              {selectedSub ? selectedSub.subCategoryName : `All ${catName} Services`}
            </SectionH2>
            {!loading ? (
              <SectionCount>
                {displayed.length} service{displayed.length !== 1 ? 's' : ''}
              </SectionCount>
            ) : null}
          </SectionTitleRow>

          <CcGrid
            key={selectedSub?.subCategoryID ?? 'all'}
            style={{ gap: 16, padding: '4px 20px 12px', alignItems: 'stretch' }}
          >
            {/* MYOP — first card (Glam & Glow, "All" view), styled like the others */}
            {!loading && isBeauty && !selectedSub && (
              <CcCell
                style={{ flex: '0 0 252px', minWidth: 252, cursor: 'pointer' }}
                onClick={() => setPkgOpen(true)}
              >
                <CcCard>
                  <div style={{ height: 184, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                    <CcCardPhoto src={MYOP_IMG} alt="Make your own package" onError={e => { e.target.src = DEFAULT_IMG; }} />
                  </div>
                  <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <p style={{ margin: '0 0 auto', fontSize: 15, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
                      Make Your Own Package
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 14 }}>
                      <ExploreBtn style={{ padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: '#111827', color: '#ffffff' }}>
                        Build package →
                      </ExploreBtn>
                    </div>
                  </div>
                </CcCard>
              </CcCell>
            )}

            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : displayed.map((s, i) => (
                  <SubCatCard
                    key={s.subCategoryID}
                    sub={s}
                    index={i}
                    accent={accent}
                    isSelected={selectedSub?.subCategoryID === s.subCategoryID}
                    onSelect={handleSelect}
                  />
                ))
            }
          </CcGrid>

          <CcMobileHint style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', margin: '4px 0 0' }}>
            swipe to see more →
          </CcMobileHint>
        </CardsSection>

        {/* ── Inline services section (appears after tapping a subcategory) ── */}
        {selectedSub && (
          <ServicesSection ref={servicesRef} $accent={accent}>
            <Wrap>
              {/* Section header */}
              <SvcSectionHeader>
                <SvcSectionIcon $accent={accent}>
                  {style.icon}
                </SvcSectionIcon>
                <SvcSectionTitleWrap>
                  <SvcSectionH2>
                    {selectedSub.subCategoryName}
                  </SvcSectionH2>
                  {!svcLoading && !svcError && (
                    <SvcSectionCount>
                      {svcList.length} service{svcList.length !== 1 ? 's' : ''} available
                    </SvcSectionCount>
                  )}
                </SvcSectionTitleWrap>
                <CloseBtn onClick={() => setSelectedSub(null)}>
                  ✕ Close
                </CloseBtn>
              </SvcSectionHeader>

              {/* Error */}
              {svcError && (
                <SvcErrorBox>
                  <p style={{ margin: 0, fontSize: 14 }}>Failed to load: {svcError}</p>
                </SvcErrorBox>
              )}

              {/* Service cards */}
              <SvcGrid style={{ gap: 14, padding: '4px 20px 8px', alignItems: 'stretch' }}>
                {svcLoading
                  ? Array.from({ length: 5 }).map((_, i) => <SvcSkel key={i} />)
                  : svcList.map((svc, i) => (
                      <SvcCard
                        key={`${svc.serviceID ?? 'svc'}-${i}`}
                        svc={svc}
                        index={i}
                        accent={accent}
                        category={category}
                        subCategoryID={selectedSub.subCategoryID}
                      />
                    ))
                }
              </SvcGrid>

              {!svcLoading && !svcError && svcList.length === 0 && (
                <SvcEmpty>
                  No services found for this category.
                </SvcEmpty>
              )}

              <SvcMobileHint style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>
                swipe to see more →
              </SvcMobileHint>
            </Wrap>
          </ServicesSection>
        )}

        {/* ── Why City Craft ── */}
        <TrustSection>
          <Wrap>
            <TrustH3>
              Why City Craft?
            </TrustH3>
            {/* <TrustSub>
              Trusted by 12M+ customers across India
            </TrustSub> */}

            <TrustGrid>
              {[
                { emoji: '🛡️', title: 'Verified Experts',    body: 'Background-checked & trained professionals' },
                { emoji: '💳', title: 'Transparent Pricing', body: 'No hidden charges. What you see is what you pay' },
                { emoji: '⭐', title: '4.8★ Rated',          body: 'Consistently top-rated by millions of customers' },
                // { emoji: '🔄', title: 'Free Re-service',     body: "Not happy? We come back and fix it at no charge" },
              ].map((item, i) => (
                <WhyCard
                  key={i}
                  $delay={i * 0.06}
                >
                  <WhyIcon>
                    {item.emoji}
                  </WhyIcon>
                  <div>
                    <WhyTitle>{item.title}</WhyTitle>
                    <WhyBody>{item.body}</WhyBody>
                  </div>
                </WhyCard>
              ))}
            </TrustGrid>
          </Wrap>
        </TrustSection>

        <Footer />
      </Page>

      <MakeYourOwnPackageModal
        open={pkgOpen}
        onClose={() => setPkgOpen(false)}
        pkg={packageData}
        ctx={{
          categoryName: catName,
          categorySlug: category,
          subCategoryID: subCategories[0]?.subCategoryID ?? null,
          serviceID: null,
        }}
      />
    </>
  );
};

/* "Why City Craft" card — has slideUp entrance animation with per-index delay */
const WhyCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  animation: ${slideUp} 0.32s ${p => p.$delay}s both;
`;

export default CategoryPage;
